import { useTranslation } from 'react-i18next';
import type { AllocationFilterState, FilterKey } from '../hooks/useAllocationFilters';
import { usePercentageRangeLabel } from '../hooks/usePercentageRangeLabel';
import { useProfessionalsChip } from '../hooks/useProfessionalsChip';
import { formatDisplayDate } from '../utils/data';
import { type FilterChip, FilterChipList } from './FilterChipList';

/**
 * Um chip por filtro aplicado (coluna ou avançado), removível, e o "Limpar
 * filtros". A faixa de % e o período viram um chip só cada.
 */
export function AppliedFilterChips({ filterState }: { filterState: AllocationFilterState }) {
  const { t, i18n } = useTranslation(['alocacao', 'common']);
  const { filters, professionals, tags, project, contract, client } = filterState;
  const percentageRangeLabel = usePercentageRangeLabel();
  const professionalsChip = useProfessionalsChip();

  const chips: FilterChip<FilterKey>[] = [];
  const contains = (key: FilterKey, field: string, value: string | undefined) => {
    if (value) chips.push({ key, text: t('filtros.chips.contem', { field, value }) });
  };

  contains('projectSearch', t('tabela.projeto'), filters.projectSearch);
  contains('contractSearch', t('tabela.contrato'), filters.contractSearch);
  contains('clientSearch', t('tabela.cliente'), filters.clientSearch);
  contains('accountDirectorSearch', t('tabela.diretorConta'), filters.accountDirectorSearch);
  contains('operationManagerSearch', t('tabela.gerenteOperacao'), filters.operationManagerSearch);
  contains('projectManagerSearch', t('tabela.gerenteProjeto'), filters.projectManagerSearch);
  if (tags.length > 0) {
    chips.push({
      key: 'tags',
      text: t('filtros.chips.selecionado', {
        field: t('form.tags'),
        name: tags.map((tag) => tag.name).join(', '),
      }),
    });
  }

  const percentage = percentageRangeLabel(filters.minPercentage, filters.maxPercentage);
  if (percentage) chips.push({ key: 'percentage', text: percentage });
  if (filters.billable !== undefined) {
    chips.push({
      key: 'billable',
      text: t('filtros.chips.selecionado', {
        field: t('tabela.faturando'),
        name: filters.billable ? t('common:yes') : t('common:no'),
      }),
    });
  }

  const professionalsText = professionalsChip(professionals);
  if (professionalsText) chips.push({ key: 'professional', ...professionalsText });
  const selected = [
    ['project', t('filtros.projeto'), project],
    ['contract', t('filtros.contrato'), contract],
    ['client', t('filtros.cliente'), client],
  ] as const;
  for (const [key, field, option] of selected) {
    if (option)
      chips.push({ key, text: t('filtros.chips.selecionado', { field, name: option.name }) });
  }
  contains('notes', t('filtros.anotacoes'), filters.notes);

  const start = filters.startDate && formatDisplayDate(filters.startDate, i18n.language);
  const end = filters.endDate && formatDisplayDate(filters.endDate, i18n.language);
  if (start && end) {
    chips.push({ key: 'period', text: t('filtros.chips.periodoEntre', { start, end }) });
  } else if (start) {
    chips.push({ key: 'period', text: t('filtros.chips.periodoDesde', { start }) });
  } else if (end) {
    chips.push({ key: 'period', text: t('filtros.chips.periodoAte', { end }) });
  }

  return (
    <FilterChipList
      chips={chips}
      onRemove={filterState.clearFilter}
      onClear={filterState.clearFilters}
    />
  );
}
